import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { PessoaDataService } from '@nomoh-monorepo/data-access-shared-services';
import { IRedefinirSenhaModel } from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subscription } from 'rxjs';
import { FormFieldComponent } from '../../form-field/form-field.component';

@Component({
  selector: 'lib-redefinir-senha-conta',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    ProgressSpinnerModule,
    FormFieldComponent,
  ],
  templateUrl: './redefinir-senha-conta.component.html',
  styleUrl: './redefinir-senha-conta.component.scss',
})
export class RedefinirSenhaContaComponent implements OnInit, OnDestroy {
  email = input.required<string>();
  telefone = input('');
  fechar = output<void>();

  private fb = inject(FormBuilder);
  private pessoaDataService = inject(PessoaDataService);
  private notificacao = inject(NotificacaoService);
  private translate = inject(TranslateService);

  form!: FormGroup;
  visible = true;
  $loadingEnvio = signal(false);
  $loadingSalvar = signal(false);

  hasLowerCase = signal(false);
  hasUpperCase = signal(false);
  hasNumber = signal(false);
  hasSpecialChar = signal(false);
  hasMinLength = signal(false);

  private subs = new Subscription();

  ngOnInit(): void {
    this.criarFormulario();
    void this.enviarToken();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get senhaValida(): boolean {
    return (
      this.hasLowerCase() &&
      this.hasUpperCase() &&
      this.hasNumber() &&
      this.hasSpecialChar() &&
      this.hasMinLength()
    );
  }

  get senhasIguais(): boolean {
    const senha = this.form.get('senha')?.value;
    const confirmar = this.form.get('confirmarSenha')?.value;
    return !!senha && !!confirmar && senha === confirmar;
  }

  get textoAjudaCodigo(): string {
    const prefixo = this.translate.instant(
      'PAGINA_RECUPERAR_SENHA.CAMPO_TEXTO.DIGITE_CODIGO_VERIFICACAO'
    );
    return `${prefixo}${this.email()}`;
  }

  private criarFormulario(): void {
    this.form = this.fb.group(
      {
        token: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
          ],
        ],
        senha: ['', [Validators.required, this.validadorSenhaCompleta]],
        confirmarSenha: ['', [Validators.required]],
      },
      { validators: this.validadorSenhasIguais }
    );

    this.subs.add(
      this.form.get('senha')!.valueChanges.subscribe((senha: string) => {
        this.validarRequisitosSenha(senha);
      })
    );
    this.subs.add(
      this.form.get('confirmarSenha')!.valueChanges.subscribe(() => {
        this.validarConfirmacaoSenha();
      })
    );
  }

  private validadorSenhaCompleta = (control: { value: string }) => {
    const senha = control.value;
    if (!senha) return null;
    this.validarRequisitosSenha(senha);
    return this.senhaValida ? null : { senhaIncompleta: true };
  };

  private validadorSenhasIguais = (formGroup: FormGroup) => {
    const senha = formGroup.get('senha')?.value;
    const confirmarSenha = formGroup.get('confirmarSenha')?.value;
    if (senha && confirmarSenha && senha !== confirmarSenha) {
      formGroup.get('confirmarSenha')?.setErrors({ passwordsMismatch: true });
      return { passwordsMismatch: true };
    }
    return null;
  };

  private validarRequisitosSenha(senha: string): void {
    if (!senha) {
      this.hasLowerCase.set(false);
      this.hasUpperCase.set(false);
      this.hasNumber.set(false);
      this.hasSpecialChar.set(false);
      this.hasMinLength.set(false);
      return;
    }
    this.hasLowerCase.set(/[a-z]/.test(senha));
    this.hasUpperCase.set(/[A-Z]/.test(senha));
    this.hasNumber.set(/[0-9]/.test(senha));
    this.hasSpecialChar.set(/[!@#$%^&*]/.test(senha));
    this.hasMinLength.set(senha.length >= 8);
  }

  private validarConfirmacaoSenha(): void {
    const senha = this.form.get('senha')?.value;
    const confirmarSenha = this.form.get('confirmarSenha')?.value;
    if (confirmarSenha && senha !== confirmarSenha) {
      this.form.get('confirmarSenha')?.setErrors({ passwordsMismatch: true });
    } else if (confirmarSenha && senha === confirmarSenha) {
      const errors = this.form.get('confirmarSenha')?.errors;
      if (errors) {
        delete errors['passwordsMismatch'];
        this.form
          .get('confirmarSenha')
          ?.setErrors(Object.keys(errors).length ? errors : null);
      }
    }
  }

  async enviarToken(): Promise<void> {
    const email = this.email()?.trim();
    if (!email) {
      this.notificacao.notificacaoError(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.ERRO_IDENTIFICAR_EMAIL'
        )
      );
      this.onCancelar();
      return;
    }

    this.$loadingEnvio.set(true);
    try {
      await this.pessoaDataService.recuperarToken(email);
      this.notificacao.notificacaoSuccess(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.MENSAGEM_SUCESSO_RECUPERAR_SENHA'
        )
      );
    } catch {
      this.notificacao.notificacaoError(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.MENSAGEM_ERRO_ENVIAR_CODIGO_VERIFICACAO'
        )
      );
      this.onCancelar();
    } finally {
      this.$loadingEnvio.set(false);
    }
  }

  async reenviarCodigo(): Promise<void> {
    await this.enviarToken();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.$loadingSalvar.set(true);
    try {
      const data = this.form.getRawValue();
      const payload: IRedefinirSenhaModel = {
        email: this.email(),
        telefone: this.telefone() || '',
        token: data.token,
        senha: data.senha,
        confirmarSenha: data.confirmarSenha,
      };
      await this.pessoaDataService.alterarSenha(payload);
      this.notificacao.notificacaoSuccess(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.MENSAGEM_SUCESSO_SENHA_REDEFINIDA'
        )
      );
      this.onCancelar();
    } catch {
      // interceptor/notificação já trata erros HTTP
    } finally {
      this.$loadingSalvar.set(false);
    }
  }

  onCancelar(): void {
    this.visible = false;
    this.fechar.emit();
  }
}
